import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  itemMasterService,
  type ItemListFilters,
  type ItemPatchRequest,
} from '../../services/api/itemMasterService';

export const fetchItemList = createAsyncThunk(
  'itemMaster/fetchList',
  async (filters: ItemListFilters, { rejectWithValue }) => {
    try {
      return await itemMasterService.listItems(filters);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch items');
    }
  }
);

export const fetchItemDetail = createAsyncThunk(
  'itemMaster/fetchDetail',
  async (
    { tenantId, id, buyerPartyId, supplierPartyId }:
      { tenantId: number; id: number; buyerPartyId?: number | null; supplierPartyId?: number | null },
    { rejectWithValue }
  ) => {
    try {
      // edi-master-data-scoped-self-serve: forward the row's pair so party-scoped
      // EDI readers are authorized on the detail endpoint.
      return await itemMasterService.getItem(tenantId, id, { buyerPartyId, supplierPartyId });
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch item');
    }
  }
);

export const fetchItemAlternateKeys = createAsyncThunk(
  'itemMaster/fetchAlternateKeys',
  async (
    { tenantId, itemId, pageIndex = 1, pageSize = 50 }:
      { tenantId: number; itemId: number; pageIndex?: number; pageSize?: number },
    { rejectWithValue }
  ) => {
    try {
      return await itemMasterService.listAlternateKeys(tenantId, itemId, pageIndex, pageSize);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch alternate keys');
    }
  }
);

export const patchItem = createAsyncThunk(
  'itemMaster/patch',
  async (
    { tenantId, id, request, suppressErrorToast }:
      { tenantId: number; id: number; request: ItemPatchRequest; suppressErrorToast?: boolean },
    { rejectWithValue }
  ) => {
    try {
      return await itemMasterService.patchItem(tenantId, id, request, { suppressErrorToast });
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update item');
    }
  }
);

// `searchItems` is a thin alias over `listItems` for the ItemPicker hook —
// keeps thunk-name discoverability aligned with the search verb.
export const searchItems = createAsyncThunk(
  'itemMaster/search',
  async (filters: ItemListFilters, { rejectWithValue }) => {
    try {
      const r = await itemMasterService.listItems(filters);
      return r.items;
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to search items');
    }
  }
);
